// utils/sentry.js - Frontend Sentry utility matching backend privacy approach
import * as Sentry from "@sentry/react";

// 🔐 Privacy protection utilities matching backend approach
const hashUserId = (userId) => {
    if (!userId) return 'anonymous';
    // Simple hash for frontend (matches backend style but lighter for performance)
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        const char = userId.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return 'user_' + Math.abs(hash).toString(16).substring(0, 8);
};

const anonymizeIP = (ip) => {
    if (!ip) return 'unknown';
    if (ip.includes('.')) {
        // IPv4 - anonymize last octet (matches backend)
        const parts = ip.split('.');
        if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.xxx`;
    } else if (ip.includes(':')) {
        // IPv6 - keep first 3 groups, anonymize the rest (matches backend)
        const parts = ip.split(':');
        if (parts.length >= 3) return `${parts[0]}:${parts[1]}:${parts[2]}:xxxx:xxxx:xxxx:xxxx:xxxx`;
    }
    return 'anonymized';
};

// Initialize Sentry with enterprise-grade privacy protection
export const initSentry = () => {
    console.log('🔍 Initializing Sentry with privacy protection...');
    console.log('Sentry DSN available:', !!process.env.REACT_APP_SENTRY_DSN);
    console.log('Environment:', process.env.NODE_ENV);

    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,

        // 🔒 SECURITY: Only enable in production (matches backend)
        enabled: process.env.NODE_ENV === 'production',

        // 🔒 SECURITY: Set environment explicitly (matches backend)
        environment: process.env.NODE_ENV || 'development',

        // 🔒 SECURITY: Reduced sampling for cost control (matches backend)
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0,

        // 🔒 SECURITY: Disable automatic session tracking (privacy)
        autoSessionTracking: false,

        // 🔒 SECURITY: Disable default PII (matches backend)
        sendDefaultPii: false,

        // 🔒 SECURITY: Attach stack traces only for errors (matches backend)
        attachStacktrace: true,

        // 🔒 SECURITY: Set maximum breadcrumbs to limit data (matches backend)
        maxBreadcrumbs: 10,

        // 🚀 PERFORMANCE: Enable performance monitoring
        integrations: [
            Sentry.browserTracingIntegration({
                // Limit traced operations
                tracePropagationTargets: [
                    "localhost",
                    "appv2.intouchwe.com",
                    "v2.api.intouchwe.com",
                    /^https:\/\/.*\.intouchwe\.com/
                ],
            }),
            Sentry.replayIntegration({
                // Only capture replays on errors for privacy
                sessionSampleRate: 0,
                errorSampleRate: process.env.NODE_ENV === 'production' ? 0.05 : 0, // Very limited
            }),
        ],

        // 🆕 ENHANCED: Maximum data filtering with privacy protection (matches backend approach)
        beforeSend(event, hint) {
            console.log('🔍 Sentry beforeSend filter applied');

            // 🔒 Remove ALL sensitive user data (matches backend)
            if (event.user) {
                const originalUserId = event.user.id || event.user.userId;
                // Keep only hashed, non-sensitive user ID for tracking
                event.user = {
                    id: originalUserId ? hashUserId(originalUserId) : 'anonymous'
                };
                // Remove ALL other user data
                delete event.user.email;
                delete event.user.username;
                delete event.user.ip_address;
                delete event.user.name;
            }

            // 🔒 Remove ALL sensitive request data (matches backend)
            if (event.request) {
                // Remove ALL headers (they can contain tokens, auth, etc.)
                delete event.request.headers;

                // Remove ALL cookies
                delete event.request.cookies;

                // Remove ALL request data/body
                delete event.request.data;

                // Remove query parameters (can contain tokens) - matches backend
                if (event.request.url) {
                    try {
                        const url = new URL(event.request.url);
                        url.search = ''; // Remove query parameters
                        url.hash = ''; // Remove hash parameters
                        event.request.url = url.pathname; // Keep only the path
                    } catch (e) {
                        // If URL parsing fails, just keep the pathname
                        event.request.url = event.request.url.split('?')[0].split('#')[0];
                    }
                }

                // Keep only safe request info (matches backend approach)
                const safeRequest = {
                    method: event.request.method, // Safe: GET, POST, etc.
                    url: event.request.url // Safe: only the path, no query params
                };
                event.request = safeRequest;
            }

            // 🔒 Remove server/environment info that could be sensitive (matches backend)
            if (event.server_name) {
                event.server_name = 'hidden-for-security';
            }

            // 🔒 Filter out sensitive data from contexts (matches backend)
            if (event.contexts) {
                // Remove device info
                delete event.contexts.device;

                // Remove detailed browser info
                if (event.contexts.browser) {
                    event.contexts.browser = {
                        name: event.contexts.browser.name, // Keep only browser name
                        version: event.contexts.browser.version ?
                            event.contexts.browser.version.split('.')[0] : 'unknown' // Keep only major version
                    };
                }

                // Remove OS details (matches backend)
                if (event.contexts.os) {
                    event.contexts.os = {
                        name: event.contexts.os.name, // Keep only OS name, remove versions
                    };
                }

                // Remove runtime details except name
                if (event.contexts.runtime) {
                    event.contexts.runtime = {
                        name: event.contexts.runtime.name, // Keep only runtime name
                    };
                }

                // Remove local storage and session storage data
                delete event.contexts.browser?.local_storage;
                delete event.contexts.browser?.session_storage;
            }

            // 🔒 Remove ALL extra data that might contain sensitive info (matches backend)
            delete event.extra;

            // 🔒 Filter breadcrumbs to remove sensitive data (matches backend approach)
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
                    // Remove data from network breadcrumbs
                    if (breadcrumb.category === 'xhr' ||
                        breadcrumb.category === 'fetch' ||
                        breadcrumb.category === 'http') {
                        delete breadcrumb.data;
                    }

                    // Remove detailed click data
                    if (breadcrumb.category === 'ui.click' ||
                        breadcrumb.category === 'ui.input') {
                        delete breadcrumb.data;
                        delete breadcrumb.message;
                    }

                    // Remove console log data
                    if (breadcrumb.category === 'console') {
                        delete breadcrumb.data;
                    }

                    // Keep only safe breadcrumb info (matches backend)
                    return {
                        message: breadcrumb.message?.substring(0, 100) || 'action', // Truncate messages
                        category: breadcrumb.category,
                        level: breadcrumb.level,
                        timestamp: breadcrumb.timestamp
                    };
                });
            }

            // 🔒 Clean the stack trace to remove file paths that might reveal structure (matches backend)
            if (event.exception && event.exception.values) {
                event.exception.values.forEach(exception => {
                    if (exception.stacktrace && exception.stacktrace.frames) {
                        exception.stacktrace.frames = exception.stacktrace.frames.map(frame => {
                            // Keep only essential debugging info, remove full paths (matches backend)
                            return {
                                filename: frame.filename ? frame.filename.split('/').pop() : 'hidden', // Only filename, not full path
                                function: frame.function || 'unknown',
                                lineno: frame.lineno,
                                colno: frame.colno,
                                in_app: frame.in_app
                            };
                        });
                    }
                });
            }

            // 🔒 Remove fingerprint and other identifying data
            delete event.fingerprint;
            delete event.release;

            console.log('✅ Sentry event sanitized and ready to send');
            return event;
        },

        // 🔒 SECURITY: Configure allowed URLs (adjust for your domain)
        allowUrls: [
            // Only capture errors from your domains
            /https:\/\/appv2\.intouchwe\.com/,
            /https:\/\/.*\.intouchwe\.com/,
            /https:\/\/staging-touch-v2\.web\.app/,
            /https:\/\/staging-touch-v2-.*\.ondigitalocean\.app/,
            // Add localhost for development testing
            /http:\/\/localhost/,
        ],

        // 🔒 SECURITY: Ignore certain errors (reduce noise and privacy)
        ignoreErrors: [
            // Ignore common browser errors that aren't actionable
            'Network request failed',
            'NetworkError',
            'Failed to fetch',
            'Load failed',
            'Script error',
            'Non-Error promise rejection captured',
            'ResizeObserver loop limit exceeded',
            'AbortError',
            'NotAllowedError',

            // Ignore media/camera errors (privacy)
            /camera/i,
            /microphone/i,
            /media/i,
            /getUserMedia/i,

            // Ignore extension errors
            /extension/i,
            /moz-extension/i,
            /chrome-extension/i,

            // Ignore ad blocker errors
            /adblock/i,
            /ublock/i,

            // Ignore cross-origin errors
            'SecurityError',
            'Permission denied',

            // Add other errors you want to ignore
        ],

        // 🔒 SECURITY: Ignore certain URLs that might contain sensitive data
        denyUrls: [
            // Ignore errors from browser extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^moz-extension:\/\//i,

            // Ignore errors from analytics/tracking scripts
            /google-analytics\.com/i,
            /googletagmanager\.com/i,
            /facebook\.net/i,
            /twitter\.com/i,
        ],

        // 🚀 Default tags for filtering (matches backend approach)
        initialScope: {
            tags: {
                platform: 'frontend',
                service: 'web-app',
                privacy_protected: 'true'
            }
        }
    });

    // Add global error handlers for additional coverage
    window.addEventListener('unhandledrejection', (event) => {
        // Check if it's a media-related error (privacy concern)
        if (event.reason &&
            (event.reason.message?.toLowerCase().includes('media') ||
                event.reason.message?.toLowerCase().includes('camera') ||
                event.reason.message?.toLowerCase().includes('microphone'))) {
            // Don't send media errors to Sentry for privacy
            event.preventDefault();
            console.warn('Media error suppressed for privacy:', event.reason.message);
            return;
        }

        // Let Sentry handle other unhandled rejections
        Sentry.captureException(event.reason);
    });

    console.log('✅ Sentry initialization completed with privacy protection');

    // Log configuration status
    const sentryStatus = {
        enabled: process.env.NODE_ENV === 'production',
        environment: process.env.NODE_ENV,
        dsnConfigured: !!process.env.REACT_APP_SENTRY_DSN,
        privacyProtection: 'active',
        dataFiltering: 'enabled'
    };

    console.log('📊 Sentry configuration status:', sentryStatus);

    // Make status available globally for debugging
    window.sentryStatus = sentryStatus;
};

// Export utility functions for manual error capture (with privacy protection)
export const captureError = (error, context = {}) => {
    if (process.env.NODE_ENV !== 'production') {
        console.error('Frontend Error:', error, context);
        return;
    }

    // Sanitize context before sending
    const sanitizedContext = {
        function: context.function,
        component: context.component,
        action: context.action,
        // Hash user ID if present
        userId: context.userId ? hashUserId(context.userId) : undefined,
        // Remove sensitive data
        timestamp: new Date().toISOString()
    };

    Sentry.withScope((scope) => {
        scope.setContext("error_context", sanitizedContext);
        scope.setTag("error_type", context.type || "manual");
        Sentry.captureException(error);
    });
};

export const captureMessage = (message, level = 'info', context = {}) => {
    if (process.env.NODE_ENV !== 'production') {
        console.log(`Frontend ${level.toUpperCase()}:`, message, context);
        return;
    }

    const sanitizedContext = {
        function: context.function,
        component: context.component,
        userId: context.userId ? hashUserId(context.userId) : undefined,
        timestamp: new Date().toISOString()
    };

    Sentry.withScope((scope) => {
        scope.setContext("message_context", sanitizedContext);
        scope.setTag("message_type", context.type || "manual");
        Sentry.captureMessage(message, level);
    });
};

// Test functions for verification
window.testSentry = () => {
    console.log('🧪 Testing Sentry integration...');
    captureMessage('Test message from frontend', 'info', {
        function: 'testSentry',
        component: 'sentry_utility'
    });
    console.log('✅ Test message sent to Sentry');
};

window.testSentryError = () => {
    console.log('🧪 Testing Sentry error capture...');
    try {
        throw new Error('Test error from frontend sentry utility');
    } catch (error) {
        captureError(error, {
            function: 'testSentryError',
            component: 'sentry_utility',
            type: 'test_error'
        });
        console.log('✅ Test error sent to Sentry');
    }
};

window.testSentryStatus = () => {
    const environment = process.env.NODE_ENV;
    const sentryEnabled = process.env.NODE_ENV === 'production';
    const dsnAvailable = !!process.env.REACT_APP_SENTRY_DSN;

    console.log('📊 Sentry Status Check:');
    console.log('Environment:', environment);
    console.log('Sentry enabled:', sentryEnabled);
    console.log('Sentry DSN available:', dsnAvailable);
    console.log('Privacy protection:', 'ACTIVE');
    console.log('Data filtering:', 'ENABLED');

    if (sentryEnabled) {
        console.log('✅ Sentry should be working in production');
    } else {
        console.log('ℹ️  Sentry disabled in development (as configured)');
    }

    return {
        environment,
        sentryEnabled,
        dsnAvailable,
        privacyProtected: true,
        dataFiltered: true
    };
};